import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ErrorBar, Cell
} from "recharts";
import { Zap, Clock, DollarSign, Shield, FlaskConical, Beaker } from "lucide-react";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";
import { useLanguage } from "@/contexts/LanguageContext";

/* ──────────────────────────────────────────────────────────
   NOTE: The previous scientific validation content (ELISA dilution
   series, qPCR time-course, Syrian hamster animal model) is archived
   in ScientificValidationArchive.js and can be restored if needed.
   ────────────────────────────────────────────────────────── */

/* ─── Data (numeric) ──────────────────────────────────── */
const qpcrSummary = [
  { value: 20.47, err: 2.51, fill: "#EF4444" },
  { value: 20.89, err: 0.92, fill: "#2D6FBF" },
];
const elisaSummary = [
  { value: 500, err: 165, fill: "#EF4444" },
  { value: 490, err: 180, fill: "#2D6FBF" },
];

const comparisonRowMeta = [
  { key: "speed",  icon: Clock },
  { key: "purif",  icon: FlaskConical },
  { key: "cost",   icon: DollarSign },
  { key: "shelf",  icon: Shield },
  { key: "manuf",  icon: Zap },
  { key: "immune", icon: Beaker },
];

/* ─── Shared ──────────────────────────────────────────── */
const SciTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111827] border border-[#1F2937] px-3.5 py-2.5 shadow-xl rounded-sm">
      <p className="font-mono text-[10px] text-[#6B7280] mb-1.5 uppercase tracking-wider">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color || p.fill }} />
          <span className="text-[#9CA3AF]">{p.name}:</span>
          <span className="font-medium text-white">{typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

function FigureCaption({ number, title, figLabel }) {
  return (
    <p className="mt-4 mb-1 text-xs text-[#6B7280]">
      <span className="font-mono font-medium text-[#374151]">{figLabel} {number}</span>
      <span className="mx-1.5 text-[#D1D5DB]">|</span>
      <span className="italic">{title}</span>
    </p>
  );
}

function InterpretationBox({ children, source, heading }) {
  return (
    <div className="mt-6 p-5 md:p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm">
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#EE1D25] font-mono font-semibold mb-1">{heading}</p>
      <div className="text-[13px] text-[#374151] leading-[1.85] space-y-3 font-body mt-3">
        {children}
      </div>
      {source && (
        <p className="text-[11px] text-[#9CA3AF] mt-4 pt-3 border-t border-[#E2E8F0] font-mono">{source}</p>
      )}
    </div>
  );
}

const axisStyle = { fontSize: 11, fill: "#6B7280", fontFamily: "'IBM Plex Mono', monospace" };

/* ─── Main Component ──────────────────────────────────── */
export default function ScientificValidation() {
  const [activeTab, setActiveTab] = useState("comparison");
  const { t } = useLanguage();

  const tabs = [
    { id: "comparison", label: t("sci.tabs.comparison") },
    { id: "qpcr",       label: t("sci.tabs.qpcr") },
    { id: "elisa",      label: t("sci.tabs.elisa") },
    { id: "purification", label: t("sci.tabs.purification") },
  ];

  const comparisonRows = comparisonRowMeta.map(r => ({
    ...r,
    feature:  t(`sci.comparison.rows.${r.key}F`),
    chemical: t(`sci.comparison.rows.${r.key}C`),
    uv:       t(`sci.comparison.rows.${r.key}U`),
  }));

  const qpcrData = [
    { group: t("sci.qpcr.groupActive"), ...qpcrSummary[0] },
    { group: t("sci.qpcr.groupUv"),     ...qpcrSummary[1] },
  ];
  const elisaData = [
    { group: t("sci.qpcr.groupActive"), ...elisaSummary[0] },
    { group: t("sci.qpcr.groupUv"),     ...elisaSummary[1] },
  ];

  const interpHeading = t("sci.interpretation");
  const figLabel = t("sci.fig");

  return (
    <section id="science" data-testid="science-section" className="py-24 md:py-36 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-20 max-w-3xl">
          <BilingualEyebrow en="Scientific Validation" hu="Tudom&aacute;nyos valid&aacute;ci&oacute;" className="text-[#6B7280]" />
          <BilingualHeading en="Evidence-Based Results" hu="Bizony&iacute;t&eacute;k-alap&uacute; eredm&eacute;nyek" colorClass="text-[#111827]" />
          <p className="text-base text-[#4B5563] mt-5 leading-relaxed">
            {t("sci.body")}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-0 mb-10 border-b border-[#E5E7EB]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`chart-tab-${tab.id}`}
              className={`px-4 md:px-5 py-3 text-xs md:text-sm font-medium transition-all relative ${
                activeTab === tab.id ? "text-[#111827]" : "text-[#9CA3AF] hover:text-[#6B7280]"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.div layoutId="sci-tab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#EE1D25]" transition={{ type: "spring", stiffness: 400, damping: 35 }} />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">

          {/* ── A: Technology Comparison ──── */}
          {activeTab === "comparison" && (
            <motion.div key="comparison" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
              <div className="bg-white border border-[#E5E7EB] overflow-hidden">
                <div className="p-6 md:p-8 border-b border-[#E5E7EB]">
                  <h3 className="font-heading text-lg font-semibold text-[#111827]">{t("sci.comparison.title")}</h3>
                  <p className="text-xs text-[#6B7280] mt-2">{t("sci.comparison.subtitle")}</p>
                </div>

                <div className="grid grid-cols-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <div className="p-4 text-[11px] font-mono font-semibold text-[#374151] uppercase tracking-wider">
                    {t("sci.comparison.colFeature")}
                  </div>
                  <div className="p-4 text-[11px] font-mono font-semibold text-[#9CA3AF] uppercase tracking-wider border-l border-[#E5E7EB]">
                    {t("sci.comparison.colChemical")}
                  </div>
                  <div className="p-4 text-[11px] font-mono font-semibold text-[#1A5A8A] uppercase tracking-wider border-l border-[#E5E7EB]">
                    {t("sci.comparison.colUv")}
                  </div>
                </div>

                {comparisonRows.map((row, i) => (
                  <motion.div
                    key={row.key}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className={`grid grid-cols-3 ${i < comparisonRows.length - 1 ? 'border-b border-[#F1F5F9]' : ''}`}
                  >
                    <div className="p-4 flex items-center gap-2.5">
                      <row.icon size={15} className="text-[#9CA3AF] shrink-0" />
                      <span className="text-sm font-medium text-[#111827]">{row.feature}</span>
                    </div>
                    <div className="p-4 text-sm text-[#6B7280] border-l border-[#F1F5F9] leading-relaxed">{row.chemical}</div>
                    <div className="p-4 text-sm text-[#1A5A8A] border-l border-[#F1F5F9] leading-relaxed font-medium">{row.uv}</div>
                  </motion.div>
                ))}
              </div>

              <FigureCaption number="1" title={t("sci.comparison.caption")} figLabel={figLabel} />
              <InterpretationBox heading={interpHeading}>
                <p>{t("sci.comparison.interp1")}</p>
                <p>{t("sci.comparison.interp2")}</p>
              </InterpretationBox>
            </motion.div>
          )}

          {/* ── B: qPCR Summary ──────────── */}
          {activeTab === "qpcr" && (
            <motion.div key="qpcr" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
              <div className="bg-white border border-[#E5E7EB] p-6 md:p-8">
                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-[#111827]">{t("sci.qpcr.title")}</h3>
                  <p className="text-xs text-[#6B7280] mt-2 font-mono">{t("sci.qpcr.meta")}</p>
                </div>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={qpcrData} margin={{ top: 30, right: 30, bottom: 20, left: 20 }} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="group" tick={{ fontSize: 11, fill: "#6B7280", fontFamily: "'IBM Plex Sans', sans-serif" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} interval={0} />
                    <YAxis tick={axisStyle} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} domain={[0, 28]} label={{ value: t("sci.qpcr.yLabel"), angle: -90, position: "insideLeft", fontSize: 11, fill: "#4B5563", dx: -10 }} />
                    <Tooltip content={<SciTooltip />} />
                    <Bar dataKey="value" name="Cq" radius={[4, 4, 0, 0]} animationDuration={800} barSize={80}>
                      <ErrorBar dataKey="err" width={6} strokeWidth={1.5} stroke="#374151" />
                      {qpcrData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-6 mt-2">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#EF4444]" /><span className="text-xs text-[#6B7280]">{t("sci.qpcr.legendActive")}</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#2D6FBF]" /><span className="text-xs text-[#6B7280]">{t("sci.qpcr.legendUv")}</span></div>
                </div>
              </div>

              <FigureCaption number="2" title={t("sci.qpcr.caption")} figLabel={figLabel} />
              <InterpretationBox source={t("sci.qpcr.source")} heading={interpHeading}>
                <p>{t("sci.qpcr.interp1")}</p>
                <p>{t("sci.qpcr.interp2")}</p>
              </InterpretationBox>
            </motion.div>
          )}

          {/* ── C: ELISA Summary ─────────── */}
          {activeTab === "elisa" && (
            <motion.div key="elisa" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
              <div className="bg-white border border-[#E5E7EB] p-6 md:p-8">
                <div className="mb-6">
                  <h3 className="font-heading text-lg font-semibold text-[#111827]">{t("sci.elisa.title")}</h3>
                  <p className="text-xs text-[#6B7280] mt-2 font-mono">{t("sci.elisa.meta")}</p>
                </div>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={elisaData} margin={{ top: 30, right: 30, bottom: 20, left: 20 }} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="group" tick={{ fontSize: 11, fill: "#6B7280", fontFamily: "'IBM Plex Sans', sans-serif" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} interval={0} />
                    <YAxis tick={axisStyle} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} domain={[0, 800]} label={{ value: t("sci.elisa.yLabel"), angle: -90, position: "insideLeft", fontSize: 11, fill: "#4B5563", dx: -10 }} />
                    <Tooltip content={<SciTooltip />} />
                    <Bar dataKey="value" name="ELISA" radius={[4, 4, 0, 0]} animationDuration={800} barSize={80}>
                      <ErrorBar dataKey="err" width={6} strokeWidth={1.5} stroke="#374151" />
                      {elisaData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-6 mt-2">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#EF4444]" /><span className="text-xs text-[#6B7280]">{t("sci.elisa.legendActive")}</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#2D6FBF]" /><span className="text-xs text-[#6B7280]">{t("sci.elisa.legendUv")}</span></div>
                </div>
              </div>

              <FigureCaption number="3" title={t("sci.elisa.caption")} figLabel={figLabel} />
              <InterpretationBox source={t("sci.elisa.source")} heading={interpHeading}>
                <p>{t("sci.elisa.interp1")}</p>
                <p>{t("sci.elisa.interp2")}</p>
              </InterpretationBox>
            </motion.div>
          )}

          {/* ── D: Purification ──────────── */}
          {activeTab === "purification" && (
            <motion.div key="purification" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
              <div className="bg-white border border-[#E5E7EB] p-6 md:p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center shrink-0">
                    <FlaskConical size={20} className="text-[#2D6FBF]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#111827]">{t("sci.purification.title")}</h3>
                    <p className="text-xs text-[#6B7280] mt-2 font-mono">{t("sci.purification.meta")}</p>
                  </div>
                </div>
                <div className="pl-14 space-y-4">
                  <p className="text-sm text-[#374151] leading-[1.85]">
                    {t("sci.purification.body")}
                  </p>
                  <p className="text-xs text-[#9CA3AF] leading-relaxed italic">
                    {t("sci.purification.patentNote")}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    {[
                      { label: t("sci.purification.targetL"),  value: t("sci.purification.targetV") },
                      { label: t("sci.purification.methodL"),  value: t("sci.purification.methodV") },
                      { label: t("sci.purification.outcomeL"), value: t("sci.purification.outcomeV") },
                    ].map(item => (
                      <div key={item.label} className="p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                        <p className="text-[10px] text-[#9CA3AF] font-mono uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="text-sm text-[#111827] font-medium">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </section>
  );
}
